import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { GetStartedDialog } from '@/components/landing/GetStartedDialog';
import { handleHashLinkClick } from '@/lib/hashNav';
import { Menu } from 'lucide-react';
import ivLogomark from '@/assets/iv-logomark.png.asset.json';

function Logo() {
  return (
    <Link to="/" className="flex items-center group shrink-0" aria-label="CultureLens home">
      <img
        src={ivLogomark.url}
        alt="IV Talent"
        className="h-[22px] w-[22px] rounded-[3px] object-contain opacity-90 group-hover:opacity-100 transition-opacity"
      />
    </Link>
  );
}


const sectionLinks = [
  { hash: '#how-it-works', label: 'How it works' },
  { hash: '#trust', label: 'Confidentiality' },
];

const wwaLinks = [
  { hash: '#who-its-for', label: "Who it's for" },
  { hash: '#about', label: 'About' },
  { hash: '#contact', label: 'Talk to us' },
];

export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Section links live on the homepage.
  // From other pages, use `/#anchor` so the browser loads home and scrolls natively.
  const sectionHref = (hash: string) => (onHome ? hash : `/${hash}`);

  const glass = !onHome || scrolled;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        glass ? 'nav-glass py-2' : 'bg-transparent py-4'
      }`}
    >
      <div className="max-w-content mx-auto px-6 sm:px-10 flex items-center justify-between gap-6">
        <Logo />

        <nav className="hidden lg:flex items-center gap-7 text-sm font-grotesk text-charcoal/80">
          {sectionLinks.map((l) => (
            <a
              key={l.hash}
              href={sectionHref(l.hash)}
              className="whitespace-nowrap hover:text-ink transition-colors focus:outline-none focus-visible:underline underline-offset-4"
            >
              {l.label}
            </a>
          ))}
          {wwaLinks.map((l) => (
            <Link
              key={l.hash}
              to={`/who-we-are${l.hash}`}
              onClick={handleHashLinkClick('/who-we-are', l.hash)}
              className="whitespace-nowrap hover:text-ink transition-colors focus:outline-none focus-visible:underline underline-offset-4"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2 shrink-0">
          <Button
            asChild
            variant="ghost"
            className="text-charcoal hover:text-ink hover:bg-transparent font-grotesk text-[14px]"
          >
            <Link to="/auth/signin">Sign in</Link>
          </Button>
          <GetStartedDialog>
            <Button className="bg-ink hover:bg-ink-soft text-cream rounded-full px-5 h-9 font-grotesk text-[13px] font-medium whitespace-nowrap">
              Request access
            </Button>
          </GetStartedDialog>
        </div>

        <Sheet>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="ghost" size="icon" aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-cream border-l border-hairline w-[85%]">
            <div className="mt-8 flex flex-col gap-1">
              {sectionLinks.map((l) => (
                <a
                  key={l.hash}
                  href={sectionHref(l.hash)}
                  className="px-3 py-3 rounded-lg text-charcoal font-grotesk hover:bg-cream-warm"
                >
                  {l.label}
                </a>
              ))}
              {wwaLinks.map((l) => (
                <Link
                  key={l.hash}
                  to={`/who-we-are${l.hash}`}
                  onClick={handleHashLinkClick('/who-we-are', l.hash)}
                  className="px-3 py-3 rounded-lg text-charcoal font-grotesk hover:bg-cream-warm"
                >
                  {l.label}
                </Link>
              ))}
              <div className="h-px bg-hairline my-4" />
              <Link
                to="/auth/signin"
                className="px-3 py-3 rounded-lg text-charcoal font-grotesk hover:bg-cream-warm"
              >
                Sign in
              </Link>
              <GetStartedDialog>
                <Button className="mt-2 bg-ink hover:bg-ink-soft text-cream rounded-full h-11 font-grotesk">
                  Request access
                </Button>
              </GetStartedDialog>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
